import { XIcon } from '@langchain/untitled-ui-icons';
import {
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Dialog as RadixDialog,
  DialogContent as RadixDialogContent,
  DialogContentProps as RadixDialogContentProps,
} from '@radix-ui/react-dialog';

import { ReactNode, forwardRef } from 'react';

import { cn } from '@/utils/tailwind';

interface DialogContentProps {
  title?: ReactNode;
  showClose?: boolean;
  children: ReactNode;
  className?: string;
  childrenClassName?: string;
  onPointerDownOutside?: RadixDialogContentProps['onPointerDownOutside'];
  onInteractOutside?: RadixDialogContentProps['onInteractOutside'];
}

export function Dialog({
  open,
  onOpenChange,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <RadixDialog modal={true} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay
          className={cn(
            'fixed inset-0 z-20 bg-black/30 duration-200 animate-in fade-in dark:bg-black/40',
            className
          )}
        >
          {children}
        </DialogOverlay>
      </DialogPortal>
    </RadixDialog>
  );
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      title,
      showClose = true,
      children,
      className,
      childrenClassName,
      onPointerDownOutside,
      onInteractOutside,
    },
    ref
  ) => {
    return (
      <RadixDialogContent
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-30 w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background',
          'fade-in',
          'data-[state=closed]:fade-out',
          className
        )}
        onPointerDownOutside={onPointerDownOutside}
        onInteractOutside={onInteractOutside}
      >
        {(title || showClose) && (
          <DialogTitle className="min-h-14 border-b border-b-primary p-4">
            {title && <div className="text-md font-semibold">{title}</div>}
            {showClose && (
              <DialogClose className="absolute right-4 top-4">
                <XIcon className="size-6 rounded-md hover:bg-primary-hover" />
              </DialogClose>
            )}
          </DialogTitle>
        )}

        <div className={cn('flex flex-col gap-4 p-4', childrenClassName)}>
          {children}
        </div>
      </RadixDialogContent>
    );
  }
);

DialogContent.displayName = 'DialogContent';
